import React from "react";
import ReactDOM from "react-dom/client";
import { App } from "./App";
import "./styles/tokens.css";
import "./styles/layout.css";

// lightweight-charts (PriceChart/PnlChart) schedules its own canvas redraws
// via requestAnimationFrame — e.g. setVisibleRange() doesn't repaint
// synchronously, it queues a redraw for the next frame and returns. If the
// chart gets torn down (React unmounts it on a range/theme/mode switch —
// see PriceChart.tsx's create effect) before that queued frame runs, the
// redraw throws "Object is disposed" from INSIDE requestAnimationFrame's
// own callback — a separate browser task with no application call stack
// underneath it, so no try/catch anywhere in this app's code can ever be
// "around" it. Confirmed live via DevTools: the stack is entirely
// {syncRange -> subscribeVisibleTimeRangeChange callback} on the CALLING
// side (which HAS a try/catch, see Overview.tsx's syncRange — it just
// isn't in the dynamic scope of the actual throw), with the throw itself
// inside lightweight-charts' own canvas-element-bitmap-size module.
// Narrowly scoped to this exact error message — every other uncaught error
// still reaches the console/dev overlay/error reporting normally; this
// only silences a specific, confirmed-harmless async race in a third-party
// dependency the app has no hook into. Matched on event.error.message (the
// bare "Object is disposed", lightweight-charts' own literal Error text),
// NOT event.filename — in a production build event.filename is the bundled
// asset's own URL, not the pre-bundle module name ("canvas-element-bitmap-
// size.mjs" only appears in DevTools because of source maps), so filename
// matching would silently never fire outside a sourcemapped dev build.
//
// NOT "Value is null" — that one LOOKED like the same shape (also thrown
// from inside a requestAnimationFrame callback rooted at createChart(), see
// git history 2026-08-19) but turned out to be a real bug, not a harmless
// race: Overview.tsx's chartData mapping floors sub-second market_metrics
// timestamps to whole seconds, and with several bot instances now writing
// near-simultaneous samples (multi-bot support), that flooring could tie
// two different bots' points onto the same second — lightweight-charts'
// line/area series require STRICTLY ascending `time` and silently fail to
// paint on a tie (setData() doesn't throw), which is what actually made
// "посл. 7д" + "Линия" render 100% blank. Fixed at the source (dedup in
// Overview.tsx's chartData memo) instead of suppressed here — don't
// re-add "Value is null" to this filter without first re-confirming it's
// this exact teardown race and not another data-shape bug.
window.addEventListener("error", (event) => {
  if (event.error instanceof Error && event.error.message === "Object is disposed") {
    event.preventDefault();
  }
});

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
